import React, { useEffect, useState } from 'react';
import { databaseService } from '../../services/database.service';
import { storageService } from '../../services/storage.service';
import { i18nService } from '../../services/i18n.service';
import AdminLayout from '../../components/layout/AdminLayout';
import Card from '../../components/common/Card';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import LanguageTabs from '../../components/common/LanguageTabs';
import toast from 'react-hot-toast';

const SettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentLang, setCurrentLang] = useState('nl');
  const [companyInfo, setCompanyInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address: { nl: '', fr: '', de: '', en: '' },
    socialMedia: {
      facebook: '',
      instagram: '',
      tiktok: '',
      linkedin: ''
    }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const data = await databaseService.getCompanyInfo();
      if (data) {
        setCompanyInfo({
          name: data.name || '',
          email: data.email || '',
          phone: data.phone || '',
          address: i18nService.extractMultiLangField(data, 'address'),
          socialMedia: data.socialMedia || {
            facebook: '',
            instagram: '',
            tiktok: '',
            linkedin: ''
          }
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const dataToSave = {
        name: companyInfo.name,
        email: companyInfo.email,
        phone: companyInfo.phone,
        ...i18nService.setTranslatedFields('address', companyInfo.address),
        socialMedia: companyInfo.socialMedia
      };
      await databaseService.update('website/company', dataToSave);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600">Manage your website settings</p>
        </div>

        <Card>
          <h2 className="text-xl font-semibold mb-4">Company Information</h2>
          <div className="space-y-4">
            <Input
              label="Company Name"
              value={companyInfo.name}
              onChange={(e) => setCompanyInfo({ ...companyInfo, name: e.target.value })}
              placeholder="Lala's Decoration"
            />

            <Input
              label="Email"
              type="email"
              value={companyInfo.email}
              onChange={(e) => setCompanyInfo({ ...companyInfo, email: e.target.value })}
              placeholder="info@example.com"
            />

            <Input
              label="Phone"
              value={companyInfo.phone}
              onChange={(e) => setCompanyInfo({ ...companyInfo, phone: e.target.value })}
              placeholder="+355 XX XXX XXX"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
              <LanguageTabs
                currentLang={currentLang}
                onLangChange={setCurrentLang}
              />
              <Input
                label={`Address (${currentLang.toUpperCase()})`}
                value={companyInfo.address[currentLang]}
                onChange={(e) => setCompanyInfo({
                  ...companyInfo,
                  address: { ...companyInfo.address, [currentLang]: e.target.value }
                })}
                placeholder="Full business address"
                helperText="Enter address in the selected language"
              />
            </div>
          </div>
        </Card>

        <Card>
          <h2 className="text-xl font-semibold mb-4">Social Media</h2>
          <div className="space-y-4">
            <Input
              label="Facebook URL"
              value={companyInfo.socialMedia?.facebook || ''}
              onChange={(e) =>
                setCompanyInfo({
                  ...companyInfo,
                  socialMedia: { ...companyInfo.socialMedia, facebook: e.target.value }
                })
              }
              placeholder="https://facebook.com/yourpage"
            />

            <Input
              label="Instagram URL"
              value={companyInfo.socialMedia?.instagram || ''}
              onChange={(e) =>
                setCompanyInfo({
                  ...companyInfo,
                  socialMedia: { ...companyInfo.socialMedia, instagram: e.target.value }
                })
              }
              placeholder="https://instagram.com/yourpage"
            />

            <Input
              label="TikTok URL"
              value={companyInfo.socialMedia?.tiktok || ''}
              onChange={(e) =>
                setCompanyInfo({
                  ...companyInfo,
                  socialMedia: { ...companyInfo.socialMedia, tiktok: e.target.value }
                })
              }
              placeholder="https://tiktok.com/@yourpage"
            />

            <Input
              label="LinkedIn URL"
              value={companyInfo.socialMedia?.linkedin || ''}
              onChange={(e) =>
                setCompanyInfo({
                  ...companyInfo,
                  socialMedia: { ...companyInfo.socialMedia, linkedin: e.target.value }
                })
              }
              placeholder="https://linkedin.com/company/yourcompany"
            />
          </div>
        </Card>

        <div className="flex justify-end">
          <Button variant="primary" onClick={handleSave} loading={saving} disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default SettingsPage;
